import { catalogApiRef, EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import { Content, InfoCard } from '@backstage/core-components';
import { Grid, Box, LinearProgress, Typography } from '@material-ui/core';
import { projectPageTranslationRef } from '../../translation/project-page/project-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { ProjectSubProjects } from './ProjectSubProjects';
import { useOpenFgaPermission } from '../../../hooks/useOpenFgaPermission';

export const ProjectFormPage = () => {
    const { entity } = useEntity();
    const { t } = useTranslationRef(projectPageTranslationRef);
    const catalogApi = useApi(catalogApiRef);
    const { allowed: projectTabAllowed } = useOpenFgaPermission('can_view_details', 'project');

    // Extract relevant fields from the project entity
    const projectName = entity.metadata.name;
    const entityRef = `project:${entity.metadata.namespace}/${projectName}`;

    const { value: children } = useAsync(async () => {
        const all = await catalogApi.getEntities({
            filter: { kind: 'Project' },
        });

        return all.items.filter(e => e.spec?.parent === entityRef);
    }, [entityRef, catalogApi]);

    const {
        number,
        type,
        lifecycle,
        parent,
        projectManager,
        executionType,
        percentCompleted,
        status,
        startDatePlanned,
        endDatePlanned,
        priority,
    } = entity.spec ?? {};

    const statusColor = (statusString: string) => {
        const status = (statusString).toLowerCase();

        const color =
            status.includes('verde') ? '#4CAF50' :      // verde
                status.includes('amarillo') ? '#FFC107' :       // amarillo
                    status.includes('rojo') ? '#F44336' :        // rojo
                        '#BDBDBD';                                  // gris por defecto

        return (
            <Box display="flex" alignItems="center">
                <Box
                    style={{
                        width: 15,
                        height: 15,
                        borderRadius: '50%',
                        backgroundColor: color,
                        marginRight: 8,
                    }}
                />
                {/* {row.entity.spec?.status ?? '—'} */}
            </Box>
        );
    }

    return (
        <Content noPadding>
            <InfoCard title={t('projectForm.title')}>
                <form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Grid container spacing={1}>

                                {/* Nombre del Proyecto */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.name')}</Typography>
                                    <Typography variant="body1">{`${number as string} - ${entity.metadata.title}`}</Typography>
                                </Grid>

                                {/* Status */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.status')}</Typography>
                                    {statusColor(status as string)}
                                </Grid>

                                {/* Project Manager */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.projectManager')}</Typography>
                                    {projectManager ? <EntityRefLink
                                        entityRef={projectManager as string}
                                        defaultKind={'User'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {projectManager as string || '—'}
                                    </Typography>}
                                </Grid>

                                {/* Parent */}
                                {parent && <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.parent')}</Typography>
                                    <EntityRefLink
                                        entityRef={parent as string}
                                        defaultKind={entity.kind ?? 'Project'}
                                        defaultNamespace="default"
                                    />
                                </Grid>}

                                {/* Lifecycle */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.lifecycle')}</Typography>
                                    <Typography variant="body1">{lifecycle as string || '—'}</Typography>
                                </Grid>

                                {/* Tipo */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.type')}</Typography>
                                    <Typography variant="body1">{type as string || '—'}</Typography>
                                </Grid>

                                {/* Start Date Planned */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.startDatePlanned')}</Typography>
                                    <Typography variant="body1">{startDatePlanned as string || '—'}</Typography>
                                </Grid>

                                {/* Execution Type */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.executionType')}</Typography>
                                    <Typography variant="body1">{executionType as string || '—'}</Typography>
                                </Grid>

                                {/* End Date Planned */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.endDatePlanned')}</Typography>
                                    <Typography variant="body1">{endDatePlanned as string || '—'}</Typography>
                                </Grid>

                                {/* Prioridad */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.priority')}</Typography>
                                    <Typography variant="body1">{priority as string || '—'}</Typography>
                                </Grid>

                                {/* Percent Completed con barra */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.percentCompleted')}</Typography>

                                    <Box display="flex" alignItems="center">
                                        <LinearProgress
                                            variant="determinate"
                                            value={Number(typeof percentCompleted === 'string'
                                                ? percentCompleted.replace(',', '.')
                                                : String(percentCompleted ?? 0))}
                                            style={{ flexGrow: 1, marginRight: 8 }}
                                        />
                                        <Typography variant="body1"><strong>{percentCompleted as string}%</strong></Typography>
                                    </Box>
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </InfoCard>

            {projectTabAllowed && (children && children?.length > 0) && (<ProjectSubProjects />)}
        </Content>
    );
};